import React from "react";
import { ReactComponent as DashboardIcon } from "../../assets/svg/icon-dashboard.svg";
import { ReactComponent as ProjectsIcon } from "../../assets/svg/icon-menu-projects.svg";
import { ReactComponent as ProductIcon } from "../../assets/svg/icon-product.svg";
import { ReactComponent as VideoIcon } from "../../assets/svg/icon-video.svg";
import { ReactComponent as VideoProductionIcon } from "../../assets/svg/video-production.svg";
import { ReactComponent as UserIcon } from "../../assets/svg/icon-menu-user.svg";
import { ReactComponent as UserIconStar } from "../../assets/svg/icon-menu-user-star.svg";
import {
  IconBook,
  IconEye,
  IconPencil,
  IconReport,
  IconSales,
} from "../../utils/icons";
import { SystemRoleKey } from "../../providers/roles";

// TDC-1499 brand/client sidebar: one typed config for BRAND_ADMIN,
// BRAND_MEMBER and BRAND_TEAM_SALES, shaped like the internal nav — top-level
// parents with accordion children — so the rail gives brand users the exact
// same behavior it gives internal staff: chevron accordions on the expanded
// rail, hover flyouts on the collapsed rail. Explicit title/icon fields —
// none of the legacy JSX-label parsing navs.tsx items need.
//
// Parents follow the internal conventions (VideoProductionNav): clicking the
// row navigates to its first visible child and reveals the children, while
// the chevron toggles without navigating. sideNavModel derives that target
// per plan; a parent's `link` is only the fallback when no child shows.
//
// Visibility is declarative: an item or child without `visible` shows for
// every brand-portal role. All three roles (BRAND_ADMIN, BRAND_MEMBER,
// BRAND_TEAM_SALES) see the same options; the only gate left is the plan —
// Brands and Brand Intelligence are network-plan (Enterprise RMN / Agency)
// users only.
//
// `workspace` marks the entries that also make up the brand-portal
// WORKSPACE rail — what internal staff see inside /portal/:brandSlug/*
// (sideNavModel re-roots their links under the slug). Only the
// network-scoped sections qualify; Production / Content / Experts scope
// from the logged-in user's own brand.

export interface NavVisibilityCtx {
  role: SystemRoleKey;
  user: unknown;
  // The scoped brand is a network root (plan enterprise_rmn |
  // enterprise_agency, the API's derived rmn) — gates the Brands entry.
  isNetwork: boolean;
  plan: string | null;
}

export interface BrandNavChild {
  key: string;
  link: string;
  title: string;
  icon?: React.ReactNode;
  visible?: (ctx: NavVisibilityCtx) => boolean;
}

export interface BrandNavEntry {
  key: string;
  link: string;
  title: string;
  icon: React.ReactNode;
  beta?: boolean;
  children?: BrandNavChild[];
  visible?: (ctx: NavVisibilityCtx) => boolean;
  // Shown inside an internal user's brand-portal workspace (see above).
  workspace?: boolean;
}

export const BrandNavItems: BrandNavEntry[] = [
  {
    key: 'dashboard',
    link: '/dashboard',
    title: 'Dashboard',
    icon: <DashboardIcon />,
  },
  {
    key: 'catalog',
    // Lands on the first child.
    link: '/products',
    title: 'Catalog',
    icon: <ProductIcon />,
    workspace: true,
    children: [
      {
        key: 'catalog-products',
        link: '/products',
        title: 'Products',
        icon: <ProductIcon />,
      },
      {
        key: 'catalog-brands',
        link: '/catalog/brands',
        title: 'Brands',
        icon: <ProjectsIcon />,
        visible: ({ isNetwork }) => isNetwork,
      },
      {
        key: 'catalog-categories',
        link: '/catalog/categories',
        title: 'Categories',
        icon: <IconBook size={18} stroke="#000" />,
      },
    ],
  },
  {
    // PDP Audit retired (TDC-1499 phase 3): category reports live on
    // Catalog > Categories, brand reports on Catalog > Brands, single-PDP
    // scores on each product's PDP URLs. Brand Intelligence + the report
    // histories are what remains.
    key: 'intelligence',
    link: '/intelligence/pdp-history',
    title: 'Intelligence',
    icon: <IconEye width={20} height={20} stroke="#000" />,
    workspace: true,
    children: [
      // Brand Intelligence reports are run on the OTHER brands in a
      // network's catalog, so only network roots (Enterprise RMN / Agency)
      // get the entry. Single brands (free / standard / enterprise brand)
      // never see it; the page itself redirects them too. Listed first:
      // network users run ad-hoc reports from it, like internal sales.
      {
        key: 'intel-prep-packs',
        link: '/intelligence/prep-packs',
        title: 'Sales Intel Prep Pack',
        icon: <IconReport width={18} height={18} stroke="#000" />,
        visible: ({ isNetwork }) => isNetwork,
      },
      // Read-only histories of the reports the Catalog pages run; every
      // brand plan sees these.
      {
        key: 'intel-pdp-history',
        link: '/intelligence/pdp-history',
        title: 'PDP History',
        icon: <IconReport width={18} height={18} stroke="#000" />,
      },
      {
        key: 'intel-cv-history',
        link: '/intelligence/consumer-voice-history',
        title: 'Consumer Voice History',
        icon: <IconReport width={18} height={18} stroke="#000" />,
      },
      {
        key: 'intel-aip-history',
        link: '/intelligence/ai-presence-history',
        title: 'AI Presence History',
        icon: <IconReport width={18} height={18} stroke="#000" />,
      },
    ],
  },
  {
    key: 'opportunities',
    link: '/opportunities/strategies',
    title: 'Opportunities',
    icon: <IconSales width={20} height={20} stroke="#000" />,
    workspace: true,
    children: [
      {
        // Brand-facing name for proposals (the API/data model keeps
        // "proposal" — status, endpoints, order names).
        key: 'opp-strategies',
        link: '/opportunities/strategies',
        title: 'Strategies',
        icon: <IconPencil size={18} stroke="#000" />,
      },
    ],
  },
  {
    key: 'production',
    link: '/video-production/orders',
    title: 'Production',
    icon: <VideoProductionIcon />,
    children: [
      {
        key: 'prod-orders',
        link: '/video-production/orders',
        title: 'Orders',
        icon: <IconPencil size={18} stroke="#000" />,
      },
      {
        key: 'prod-projects',
        link: '/video-production/projects',
        title: 'Projects',
        icon: <ProjectsIcon />,
      },
    ],
  },
  {
    key: 'content',
    link: '/videos',
    title: 'Content',
    icon: <VideoIcon />,
    children: [
      {
        key: 'content-videos',
        link: '/videos',
        title: 'Videos',
        icon: <VideoIcon />,
      },
    ],
  },
  {
    key: 'experts',
    link: '/experts',
    title: 'Experts',
    icon: <UserIcon />,
    children: [
      {
        key: 'experts-mine',
        link: '/experts',
        title: 'My Experts',
        icon: <UserIcon />,
      },
      {
        key: 'experts-featured',
        link: '/experts/featured',
        title: 'Featured Experts',
        icon: <UserIconStar />,
      },
    ],
  },
];
